import React, { ReactNode } from 'react'
import styled from 'styled-components'
import { AuthFooter } from '../ui/AuthFooter'

export interface AuthLayoutProps {
  children: ReactNode
  version?: string
  brand?: string
}

export const AuthLayout: React.FC<AuthLayoutProps> = ({
  children,
  version,
  brand,
}) => (
  <Page>
    <Blob $variant="b1" />
    <Blob $variant="b2" />
    <Blob $variant="b3" />
    <Stack>
      <Card>{children}</Card>
      <AuthFooter version={version} brand={brand} />
    </Stack>
  </Page>
)
AuthLayout.displayName = 'AuthLayout'

const Page = styled.div`
  height: 100vh;
  height: 100dvh;
  /* While the on-screen keyboard is open keep the full height (see useKeyboardViewportLock). */
  html[data-vk-lock] & {
    height: var(--app-lock-height);
  }
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem 1rem;
  position: relative;
  overflow-x: hidden;
  overflow-y: auto;
  background: ${({ theme }) => theme.colors.bg};
  font-family: ${({ theme }) => theme.fonts.sans};
  color: ${({ theme }) => theme.colors.ink};

  /* Invisible scrollbar — keep scroll behavior */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    width: 0;
    height: 0;
    display: none;
  }

  @supports (align-items: safe center) {
    align-items: safe center;
  }

  @media (max-width: 479px) {
    padding: 1rem;
  }
`

const blobStyles = {
  b1: { width: '520px', height: '520px', bg: '#C5F230', top: '-120px', right: '-160px', opacity: 0.32 },
  b2: { width: '420px', height: '420px', bg: '#E8F4DA', bottom: '-80px', left: '-120px', opacity: 0.65 },
  b3: { width: '260px', height: '260px', bg: '#E0EFC4', top: '50%', left: '30%', opacity: 0.45 },
} as const

const Blob = styled.div<{ $variant: keyof typeof blobStyles }>`
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  z-index: 0;
  pointer-events: none;
  width: ${({ $variant }) => blobStyles[$variant].width};
  height: ${({ $variant }) => blobStyles[$variant].height};
  background: ${({ $variant }) => blobStyles[$variant].bg};
  opacity: ${({ $variant }) => blobStyles[$variant].opacity};
  ${({ $variant }) => {
    const s = blobStyles[$variant]
    return Object.entries(s)
      .filter(([k]) => ['top', 'right', 'bottom', 'left'].includes(k))
      .map(([k, v]) => `${k}: ${v};`)
      .join('\n')
  }}
`

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-width: 25rem;
  position: relative;
  z-index: 1;
  direction: rtl;
  text-align: right;
`

const Card = styled.div`
  width: 100%;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.line};
  border-radius: ${({ theme }) => theme.radii.lg};
  padding: 3.5rem 2.5rem 2.75rem;
  box-shadow: ${({ theme }) => theme.shadows.card};

  @media (max-width: 479px) {
    padding: 2rem 1.5rem 1.5rem;
  }
`

export default AuthLayout
